import React, { useState } from 'react';
import { FileText, Lock, CheckCircle2, X, ArrowRight, TrendingDown, Percent, CalendarClock, Clock, AlertTriangle, Layers } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RETROFIT_URL = 'https://customer-assets-wrfwihn1.emergentagent.net/job_9e1fb140-210f-412f-9430-9687aa3be1b0/artifacts/bj866f92_LLRetrofitWhitepaper-2026.pdf';
const DOWNTIME_URL = 'https://customer-assets-wrfwihn1.emergentagent.net/job_9e1fb140-210f-412f-9430-9687aa3be1b0/artifacts/ximt138g_LLDowntimeEconomics-2026.pdf';

const STUDIES = [
  {
    key: 'retrofit',
    tag: 'Whitepaper · 2026',
    title: 'Rebuild vs. Replace: The Retrofit Economics',
    desc: 'When does a rebuild beat buying new? A lifecycle-cost framework built on recoverable value, avoided capital and downtime — with real thresholds by machine class.',
    url: RETROFIT_URL,
    stats: [
      { icon: TrendingDown, value: '$10,926', label: 'Capital avoided (100 lb class)' },
      { icon: Percent, value: '46.5%', label: 'Retrofit vs. buying new' },
      { icon: CalendarClock, value: '~4.7 yrs', label: '10-year breakeven' },
    ],
  },
  {
    key: 'downtime',
    tag: 'Whitepaper · 2026',
    title: 'When the Machine Stops: The Cost of Downtime',
    desc: 'What every hour offline really costs — product at risk, missed contribution and the backlog that follows — plus a framework to plan for it before failure hits.',
    url: DOWNTIME_URL,
    stats: [
      { icon: TrendingDown, value: '$19,133', label: '7-day missed contribution (100 lb)' },
      { icon: Clock, value: '63 days', label: 'Backlog to clear at 90% use' },
      { icon: AlertTriangle, value: '2× repair', label: 'A week down can top a rebuild' },
    ],
  },
];

const STUDY_BY_KEY = Object.fromEntries(STUDIES.map((s) => [s.key, s]));

export const CaseStudies = ({ testid = 'case-studies' }) => {
  const [gateOpen, setGateOpen] = useState(false);
  const [phase, setPhase] = useState('form'); // form | unlocked
  const [email, setEmail] = useState('');
  const [selected, setSelected] = useState({ retrofit: false, downtime: false });
  const [submitting, setSubmitting] = useState(false);

  const openGate = (key) => {
    setSelected({ retrofit: key === 'retrofit', downtime: key === 'downtime' });
    setEmail('');
    setPhase('form');
    setGateOpen(true);
  };

  const toggle = (key) => setSelected((p) => ({ ...p, [key]: !p[key] }));

  const chosen = STUDIES.filter((s) => selected[s.key]);

  const submit = async () => {
    if (!email || !email.includes('@')) { toast.error('Please enter a valid email to unlock your case studies.'); return; }
    if (chosen.length === 0) { toast.error('Pick at least one case study to unlock.'); return; }
    setSubmitting(true);
    try {
      await Promise.allSettled(chosen.map((s) => axios.post(`${API_URL}/api/leads/document`, {
        email,
        document_title: s.title,
        download_url: s.url,
        category: 'case-study',
      })));
      setPhase('unlocked');
      toast.success('Unlocked! We\'ve also emailed your links.');
    } catch (e) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="py-20 section-padding bg-[#003456]" data-testid={testid}>
      <div className="max-w-7xl mx-auto">
        <div className="max-w-2xl mb-12">
          <span className="inline-flex items-center gap-2 text-[#CE8946] font-semibold text-xs uppercase tracking-[0.2em]">
            <Lock className="h-4 w-4" /> Free case studies · email unlock
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mt-4 leading-tight">
            The economics behind every rebuild
          </h2>
          <p className="text-white/70 mt-4 leading-relaxed">
            Two data-driven whitepapers on when to rebuild vs. replace, and what downtime really costs.
            Drop your email to unlock — read one or grab both.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {STUDIES.map((s) => (
            <div
              key={s.key}
              className="bg-white/[0.03] border border-white/10 rounded-2xl p-7 flex flex-col hover:border-[#CE8946]/40 transition-colors"
              data-testid={`case-study-card-${s.key}`}
            >
              <span className="text-[#CE8946] text-xs uppercase tracking-[0.2em] font-semibold">{s.tag}</span>
              <h3 className="text-white font-bold text-xl mt-2 leading-snug">{s.title}</h3>
              <p className="text-white/60 text-sm mt-3 leading-relaxed flex-1">{s.desc}</p>

              <div className="grid grid-cols-3 gap-3 mt-6">
                {s.stats.map((st) => (
                  <div key={st.value} className="bg-[#023F63] border border-white/10 rounded-xl p-3">
                    <st.icon className="h-4 w-4 text-[#CE8946] mb-1.5" />
                    <div className="text-white font-bold text-base leading-none">{st.value}</div>
                    <div className="text-white/50 text-[10px] mt-1 leading-snug">{st.label}</div>
                  </div>
                ))}
              </div>

              <button
                onClick={() => openGate(s.key)}
                className="mt-6 inline-flex items-center justify-center gap-2 bg-[#CE8946] hover:bg-[#B87333] text-[#003456] font-bold px-6 py-3 rounded-md transition-colors"
                data-testid={`case-study-unlock-${s.key}`}
              >
                <Lock className="h-4 w-4" /> Unlock &amp; read
              </button>
            </div>
          ))}
        </div>

        <p className="text-white/40 text-xs mt-6 flex items-center gap-2">
          <Layers className="h-3.5 w-3.5" /> PDF · No cost · Your email is only used to send your links and follow up if you ask.
        </p>
      </div>

      {/* Email gate modal */}
      {gateOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid={`${testid}-gate`}>
          <div className="relative bg-white rounded-2xl p-7 sm:p-8 w-full max-w-md shadow-2xl">
            <button onClick={() => setGateOpen(false)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-700" data-testid={`${testid}-gate-close`}>
              <X className="h-5 w-5" />
            </button>

            {phase === 'form' ? (
              <>
                <div className="w-12 h-12 rounded-xl bg-[#CE8946]/15 flex items-center justify-center mb-4">
                  <Lock className="h-6 w-6 text-[#B87333]" />
                </div>
                <h3 className="text-xl font-bold text-[#003456]">Unlock your free case studies</h3>
                <p className="text-slate-500 text-sm mt-1.5">Enter your email, then choose which to read — one or both.</p>

                <label className="block text-xs font-medium text-slate-600 mt-5 mb-1.5">Your email *</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full px-4 py-2.5 rounded-md border bg-white border-slate-300 text-[#003456] placeholder-slate-400 focus:ring-2 focus:ring-[#CE8946] focus:border-[#CE8946] text-sm focus:outline-none"
                  data-testid={`${testid}-gate-email`}
                />

                <p className="block text-xs font-medium text-slate-600 mt-4 mb-2">Which would you like? (select one or both)</p>
                <div className="space-y-2">
                  {STUDIES.map((s) => (
                    <label
                      key={s.key}
                      className={`flex items-start gap-3 px-3.5 py-3 rounded-md border cursor-pointer transition-colors ${selected[s.key] ? 'bg-[#CE8946]/10 border-[#CE8946]' : 'bg-slate-50 border-slate-300 hover:border-[#CE8946]/60'}`}
                      data-testid={`${testid}-gate-check-${s.key}`}
                    >
                      <input type="checkbox" checked={selected[s.key]} onChange={() => toggle(s.key)} className="h-4 w-4 mt-0.5 accent-[#CE8946]" />
                      <span className="text-sm text-[#003456] font-medium leading-snug">{s.title}</span>
                    </label>
                  ))}
                </div>

                <button
                  onClick={submit}
                  disabled={submitting}
                  className="w-full mt-5 inline-flex items-center justify-center gap-2 bg-[#CE8946] hover:bg-[#B87333] text-[#003456] font-bold px-6 py-3 rounded-md transition-colors disabled:opacity-60"
                  data-testid={`${testid}-gate-submit`}
                >
                  {submitting ? 'Unlocking…' : (<>Unlock case studies <ArrowRight className="h-4 w-4" /></>)}
                </button>
              </>
            ) : (
              <>
                <div className="w-12 h-12 rounded-xl bg-[#CE8946]/15 flex items-center justify-center mb-4">
                  <CheckCircle2 className="h-6 w-6 text-[#CE8946]" />
                </div>
                <h3 className="text-xl font-bold text-[#003456]">You're in — enjoy the read</h3>
                <p className="text-slate-500 text-sm mt-1.5">We've emailed your links to <strong className="text-[#003456]">{email}</strong>. Open them here too:</p>
                <div className="space-y-2 mt-5">
                  {chosen.map((s) => (
                    <a
                      key={s.key}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between gap-3 px-4 py-3 rounded-md border border-slate-200 hover:border-[#CE8946] hover:bg-[#CE8946]/5 transition-colors"
                      data-testid={`${testid}-gate-open-${s.key}`}
                    >
                      <span className="flex items-center gap-2 text-sm text-[#003456] font-semibold">
                        <FileText className="h-4 w-4 text-[#CE8946]" /> {s.title}
                      </span>
                      <ArrowRight className="h-4 w-4 text-[#CE8946]" />
                    </a>
                  ))}
                </div>
                <button onClick={() => setGateOpen(false)} className="w-full mt-5 border border-slate-300 text-[#003456] font-semibold px-6 py-3 rounded-md hover:bg-slate-50 transition-colors" data-testid={`${testid}-gate-done`}>
                  Done
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default CaseStudies;
